import React from 'react';
import { capitalize } from 'lodash';
import pluralize from 'pluralize';

import Button from 'components/ButtonDeprecated';
import Glyph from 'components/Glyph';
import MediaObject from 'components/MediaObject';
import Row from 'components/Row';
import Text from 'components/Text';
import isNumber from 'lodash/isNumber';
import IndividualRecipientAvatar from 'chat_v2/components/IndividualRecipientAvatar';
import GroupRecipientAvatar from 'chat_v2/components/GroupRecipientAvatar';
import GroupChatAvatar from 'chat_v2/components/GroupChatAvatar';

import { ComposerContext } from './ComposerContext';
import { ComposerDispatchContext } from 'chat_v2/ComposerContext';

import { ComposerRecipients_composerRecipients_recipients_ComposerRecipientListEntity } from 'chat_v2/components/SplitComposer/queries/__generated__/ComposerRecipients';

type Props = {
  entity: ComposerRecipients_composerRecipients_recipients_ComposerRecipientListEntity,
  filters?: { [filterType: string]: string[] },
  count?: number,
};

type State = {
  hovered: boolean,
};

export default class RecipientSelectionItem extends React.Component<Props, State> {
  state = {
    hovered: false,
  };

  renderSelectionItem = (options: {
    avatar: React.ReactNode,
    primaryText: string,
    secondaryText?: string,
    count?: number,
    eventKey: string,
    eventProperties?: Object,
    uuid: string,
  }) => {
    const { avatar, primaryText, secondaryText, count, eventKey, eventProperties, uuid } = options;
    return (
      <ComposerContext.Consumer>
        {composerContext => (
          <ComposerDispatchContext.Consumer>
            {composerDispatch => (
              <li
                className="SelectFromList-selectionItem"
                onMouseOver={() => this.setState({ hovered: true })}
                onMouseLeave={() => this.setState({ hovered: false })}>
                <Row size="small">
                  <MediaObject figure="right" gutter="xsmall" center={!secondaryText}>
                    <MediaObject gutter="small" center={!secondaryText}>
                      {avatar}
                      <div>
                        <Text size="xsmall" element="div" className="SelectFromList-selectionItem-name">
                          {primaryText}
                        </Text>
                        {secondaryText && (
                          <Text size="xxsmall" element="div" color="graphite">
                            {secondaryText}
                          </Text>
                        )}
                      </div>
                    </MediaObject>
                    <div className="SelectFromList-selectionItem-figure">
                      {this.state.hovered ? (
                        <Button
                          aria-label={`Remove ${primaryText} from selection`}
                          onClick={() => composerDispatch({ type: 'removeRecipient', uuid })}
                          level="icon"
                          data-test="close-btn"
                          eventKey={eventKey}
                          eventProperties={{
                            ...composerContext.getUIEventContext(),
                            eventProperties,
                          }}>
                          <Glyph name="close-circle" color="graphite" size="medium" solo />
                        </Button>
                      ) : (
                        <Text size="xsmall" color="graphite">
                          {isNumber(count) ? count : ''}
                        </Text>
                      )}
                    </div>
                  </MediaObject>
                </Row>
              </li>
            )}
          </ComposerDispatchContext.Consumer>
        )}
      </ComposerContext.Consumer>
    );
  };

  render() {
    const { entity, filters, count } = this.props;
    const { content } = entity;

    if (entity.recipient.type === 'individual') {
      return this.renderSelectionItem({
        avatar: <IndividualRecipientAvatar entity={entity} size="xsmall" hideBadge />,
        primaryText: content.title,
        secondaryText: capitalize(content.info ?? ''),
        count: 1,
        eventKey: 'announcements.share_to.individual.selected_list.unselect.click',
        uuid: entity.recipient.uuid,
      });
    } else if (entity.recipient.type === 'group' || entity.recipient.type === 'district') {
      const filterValues = filters?.roles || [];
      return this.renderSelectionItem({
        avatar: <GroupRecipientAvatar entity={entity} size="xsmall" />,
        primaryText: content.title,
        secondaryText: filterValues.map(v => capitalize(v)).map(v => pluralize(v)).join(', '),
        count,
        eventKey: `announcements.share_to.${entity.recipient.type === 'district' ? 'organization' : 'group'}.selected_list.unselect.click`,
        uuid: entity.recipient.uuid,
      });
    } else if (entity.recipient.type === 'group_chat') {
      return this.renderSelectionItem({
        avatar: <GroupChatAvatar size="xsmall" />,
        primaryText: content.title,
        secondaryText: capitalize(content.info ?? ''),
        // Take count out of "info" text which looks like: "4 people"
        count: content.info ? parseInt(content.info.split(' ')[0]) : undefined,
        eventKey: 'announcements.share_to.group_chat.selected_list.unselect.click',
        uuid: entity.recipient.uuid,
      });
    }

    return null;
  }
}
